import * as React from 'react';
import { Box, Button, Chip, IconButton, Paper, Stack, Typography } from '@mui/material';
import { Plus as PlusIcon, Trash as TrashIcon, PencilSimple as PencilSimpleIcon } from '@phosphor-icons/react';
import { toTitleCase } from '@/lib/v2/typography/to-title-case';
import { unitsInCommunity } from '../../lib/removal-rules';

export interface SystemUserCommunity {
  id: number | string;
  name: string;
  role?: { name?: string } | null;
  user_title?: { title?: string } | null;
}

export interface SystemUserUnit {
  id: number | string;
  unit_number?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  community?: { id: number | string; name?: string } | null;
  role?: { name?: string } | null;
  user_title?: { title?: string } | null;
  move_in_date?: string | null;
  move_out_date?: string | null;
}

interface CommunityCardProps {
  community: SystemUserCommunity;
  units: SystemUserUnit[];
  isArchived: boolean;
  onEditRole: () => void;
  onRemoveCommunity: () => void;
  onAddUnit: () => void;
  onEditUnit: (unit: SystemUserUnit) => void;
  onRemoveUnit: (unit: SystemUserUnit) => void;
}

export function CommunityCard({
  community,
  units,
  isArchived,
  onEditRole,
  onRemoveCommunity,
  onAddUnit,
  onEditUnit,
  onRemoveUnit,
}: CommunityCardProps): React.JSX.Element {
  const communityUnits = unitsInCommunity(units, community.id);

  return (
    <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden' }}>
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {community.name}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 1, alignItems: 'center' }}>
            {community.role && (
              <Chip label={toTitleCase(community.role.name ?? '')} size="small" color="primary" variant="outlined" />
            )}
            {community.user_title && (
              <Chip label={toTitleCase(community.user_title.title ?? '')} size="small" variant="outlined" />
            )}
            {!isArchived && (
              <IconButton size="small" onClick={onEditRole}>
                <PencilSimpleIcon size={14} />
              </IconButton>
            )}
          </Stack>
        </Box>
        {!isArchived && (
          <IconButton color="error" size="small" onClick={onRemoveCommunity}>
            <TrashIcon size={18} />
          </IconButton>
        )}
      </Box>

      <Box sx={{ borderTop: '1px solid', borderColor: 'divider', p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
            Units
          </Typography>
          {!isArchived && (
            <Button size="small" startIcon={<PlusIcon size={16} />} variant="text" onClick={onAddUnit}>
              Add Unit
            </Button>
          )}
        </Box>
        {communityUnits.length > 0 ? (
          <Stack spacing={1}>
            {communityUnits.map((unit) => (
              <Box
                key={unit.id}
                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {unit.unit_number ? `Unit ${unit.unit_number}` : 'Unit (no number)'}
                  </Typography>
                  {(unit.address || unit.city) && (
                    <Typography variant="caption" color="text.secondary">
                      {[unit.address, unit.city, unit.state].filter(Boolean).join(', ')}
                    </Typography>
                  )}
                  <Stack direction="row" spacing={1} sx={{ mt: 0.5, alignItems: 'center' }}>
                    {unit.role && (
                      <Chip label={toTitleCase(unit.role.name ?? '')} size="small" color="secondary" variant="outlined" />
                    )}
                    {unit.user_title && (
                      <Chip label={toTitleCase(unit.user_title.title ?? '')} size="small" variant="outlined" />
                    )}
                  </Stack>
                </Box>
                {!isArchived && (
                  <Stack direction="row" spacing={0.5}>
                    <IconButton size="small" onClick={() => onEditUnit(unit)}>
                      <PencilSimpleIcon size={14} />
                    </IconButton>
                    <IconButton color="error" size="small" onClick={() => onRemoveUnit(unit)}>
                      <TrashIcon size={16} />
                    </IconButton>
                  </Stack>
                )}
              </Box>
            ))}
          </Stack>
        ) : (
          <Typography color="text.secondary" variant="body2">
            No units in this community
          </Typography>
        )}
      </Box>
    </Paper>
  );
}
